import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  UserGroupIcon,
  MapIcon,
  CogIcon,
  ChartBarIcon,
  DocumentTextIcon,
  TableCellsIcon,
  DocumentDuplicateIcon,
  Square3Stack3DIcon,
  BellIcon,
  PhotoIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import FullLogo from "../shared/logo/FullLogo"

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', path: '/dashboard', icon: HomeIcon },
  { name: 'Users', path: '/users', icon: UserGroupIcon },
  { name: 'Map', path: '/map', icon: MapIcon },
  { name: 'Analytics', path: '/analytics', icon: ChartBarIcon },
  { name: 'Settings', path: '/settings', icon: CogIcon },
  {
    name: 'Demo',
    path: '/views',
    icon: DocumentIcon,
    children: [
      { name: 'Typography', path: '/views/typography', icon: DocumentTextIcon },
      { name: 'Tables', path: '/views/tables', icon: TableCellsIcon },
      { name: 'Forms', path: '/views/forms', icon: DocumentDuplicateIcon },
      { name: 'Shadows', path: '/views/shadows', icon: Square3Stack3DIcon },
      { name: 'Alerts', path: '/views/alerts', icon: BellIcon },
      { name: 'Icons', path: '/views/icons', icon: PhotoIcon },
      { name: 'Sample Page', path: '/views/sample-page', icon: DocumentIcon },
    ],
  },
];

const SidebarLayout: React.FC = () => {
  const location = useLocation();
  const [openMenus, setOpenMenus] = React.useState<string[]>([]);

  const toggleMenu = (path: string) => {
    setOpenMenus((prev) =>
      prev.includes(path)
        ? prev.filter((p) => p !== path)
        : [...prev, path]
    );
  };

  return (
    <aside className="w-64 bg-white dark:bg-dark border-r border-border dark:border-darkborder h-screen">
      {/* Logo */}
      <div className="h-16 flex items-center justify-center border-b border-border dark:border-darkborder">
        <FullLogo />
      </div>

      {/* Navigation */}
      <nav className="mt-5 px-2">
        <div className="space-y-1">
          {navigation.map((item) => (
            <div key={item.name}>
              {item.children ? (
                <>
                  <button
                    onClick={() => toggleMenu(item.path)}
                    className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      openMenus.includes(item.path)
                        ? 'bg-primary text-white'
                        : 'text-bodytext hover:bg-lighthover dark:hover:bg-darkmuted'
                    }`}
                  >
                    <item.icon
                      className={`mr-3 h-6 w-6 ${
                        openMenus.includes(item.path)
                          ? 'text-white'
                          : 'text-bodytext group-hover:text-primary'
                      }`}
                      aria-hidden="true"
                    />
                    {item.name}
                  </button>
                  {openMenus.includes(item.path) && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.children.map((child) => (
                        <NavLink
                          key={child.name}
                          to={child.path}
                          className={({ isActive }) =>
                            `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                              isActive
                                ? 'bg-primary text-white'
                                : 'text-bodytext hover:bg-lighthover dark:hover:bg-darkmuted'
                            }`
                          }
                        >
                          <child.icon
                            className={`mr-3 h-5 w-5 ${
                              location.pathname === child.path
                                ? 'text-white'
                                : 'text-bodytext group-hover:text-primary'
                            }`}
                            aria-hidden="true"
                          />
                          {child.name}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      isActive
                        ? 'bg-primary text-white'
                        : 'text-bodytext hover:bg-lighthover dark:hover:bg-darkmuted'
                    }`
                  }
                >
                  <item.icon
                    className={`mr-3 h-6 w-6 ${
                      location.pathname === item.path
                        ? 'text-white'
                        : 'text-bodytext group-hover:text-primary'
                    }`}
                    aria-hidden="true"
                  />
                  {item.name}
                </NavLink>
              )}
            </div>
          ))}
        </div>
      </nav>
    </aside>
  );
};

export default SidebarLayout; 